/**
 * MemoryProposalsPanel — HITL queue of memory writes an agent proposed
 * (PP-main parity gap B). Lists the signed-in user's PENDING proposals
 * (GET /api/memory-proposals); Accept writes the value into scoped_memory
 * (POST :id/accept), Reject drops it (POST :id/reject).
 *
 * Mounts in SettingsPage after MyMemoryPanel. Hidden entirely when there
 * are no pending proposals, so the page stays quiet for users who've
 * never received one (matches PP-main). Poll-on-open + manual Refresh;
 * no push notification (parity).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { SpinnerGap, Robot, ArrowsClockwise, Check, X } from '@phosphor-icons/react';
import { apiFetch } from '../../api/auth-fetch';

interface MemoryProposal {
  id: string;
  deck_id: string | null;
  agent_slug: string;
  scope: 'user' | 'workspace' | 'org';
  scope_id: string;
  memory_key: string;
  content: string;
  rationale: string | null;
  created_at: number;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { ...((init.headers as Record<string, string>) || {}) };
  if (init.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
  const res = await apiFetch(path, { ...init, headers });
  const text = await res.text();
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = JSON.parse(text); if (j?.error?.message) msg = j.error.message; else if (j?.error) msg = j.error; } catch {}
    throw new Error(msg);
  }
  if (!text) return undefined as T;
  try { return JSON.parse(text) as T; } catch { return text as unknown as T; }
}

function relativeTime(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function MemoryProposalsPanel() {
  const [proposals, setProposals] = useState<MemoryProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api<{ proposals: MemoryProposal[] }>('/api/memory-proposals');
      setProposals(r.proposals ?? []);
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const decide = useCallback(async (id: string, action: 'accept' | 'reject') => {
    setBusyId(id);
    setError('');
    try {
      await api(`/api/memory-proposals/${id}/${action}`, { method: 'POST' });
      setProposals((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      setError((e as Error)?.message ?? `Failed to ${action}`);
    } finally {
      setBusyId(null);
    }
  }, []);

  // Stay quiet when there's nothing to decide.
  if (!loading && proposals.length === 0 && !error) return null;

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)' }}>
          <Robot size={18} weight="regular" />
          Agent suggestions
          {proposals.length > 0 && (
            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>({proposals.length})</span>
          )}
        </h3>
        <button
          onClick={() => void load()}
          title="Refresh"
          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)' }}
        >
          <ArrowsClockwise size={14} /> Refresh
        </button>
      </div>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', marginBottom: 12 }}>
        Memory writes an agent suggested to your or your workspace's shared memory. Accept to save them.
      </p>
      {error && <div style={{ color: 'var(--danger, #c0392b)', fontSize: 'var(--fs-sm)', marginBottom: 8 }}>{error}</div>}
      {loading && proposals.length === 0 ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <SpinnerGap size={14} className="spin" /> Loading…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {proposals.map((p) => (
            <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 12, background: 'var(--bg-surface)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)' }}>
                <span><strong style={{ color: 'var(--text-primary)' }}>{p.agent_slug}</strong> · {p.scope}/{p.scope_id}/{p.memory_key}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{relativeTime(p.created_at)}</span>
              </div>
              {p.rationale && (
                <div style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: 'var(--fs-sm)', margin: '6px 0' }}>{p.rationale}</div>
              )}
              <pre style={{ maxHeight: 160, overflow: 'auto', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 6, padding: 8, margin: '8px 0', fontSize: 'var(--fs-sm)', whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>{p.content}</pre>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  disabled={busyId === p.id}
                  onClick={() => void decide(p.id, 'accept')}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--brand)', color: 'var(--ds-on-accent)', border: 'none', borderRadius: 8, padding: '5px 12px', cursor: busyId === p.id ? 'default' : 'pointer', fontSize: 'var(--fs-sm)', opacity: busyId === p.id ? 0.6 : 1 }}
                >
                  <Check size={14} /> Accept
                </button>
                <button
                  disabled={busyId === p.id}
                  onClick={() => void decide(p.id, 'reject')}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '5px 12px', cursor: busyId === p.id ? 'default' : 'pointer', fontSize: 'var(--fs-sm)', opacity: busyId === p.id ? 0.6 : 1 }}
                >
                  <X size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
